import { Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { AppConstants, LMTimeStampDirective } from '@utils/index';
import { LMFieldBaseComponent } from '@field/lm-field-base.component';
import { LMModel } from '@form/model';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';

enum LMDateSubTypeDisplayEnum {
  DATE = 0,
  TEXT,
  TAG
};

@Component({
  selector:    'lm-date',
  templateUrl: './lm-date.component.html',
  styleUrls:   [ 'lm-date.component.scss' ]
})
export class LMDateComponent extends LMFieldBaseComponent implements OnInit, OnDestroy {
  @Input() layoutObject: LMFormLayoutField;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;

  @HostBinding('attr.data-automation-id') get dataFieldAutomationId() {
    return `lm-${this.fieldAutomationId}-field`;
  }

  public dateFormat: string = undefined;
  public showTime: boolean = undefined;
  public timeFormat: string = undefined;
  public mode: string;

  // see DateYMD.java
  // these dates are one day less and one day more than the min and max so
  // that the datepicker UI correctly displays the correct range.
  public disableOptions: SohoDatePickerDisable = {
    dates:     '',
    minDate:   this.lmFieldServiceAdapter.lmFormatUtilService.minDate,
    maxDate:   this.lmFieldServiceAdapter.lmFormatUtilService.maxDate,
    dayOfWeek: []
  };

  public pickerOptions: SohoDatePickerOptions = {
    disable: this.disableOptions,
    incrementWithKeyboard: true,
    todayWithKeyboard: true
  };

  public displayType: LMDateSubTypeDisplayEnum;
  public layoutDisplayType;

  constructor(protected lmFieldServiceAdapter: LMFieldServiceAdapter) {
    super(lmFieldServiceAdapter);
    this.layoutDisplayType = LMDateSubTypeDisplayEnum;
  }

  contextMenuClicked = ($event) => {
    this.onContextMenuEvent($event);
  };

  ngOnInit() {
    super.ngOnInit();
    this.initialize(this.layoutObject, this.specFields, this.model);
    this.setOptions();
    this.setSize();
    this.setFieldClasses();
    this.subscribeToTimezoneField();

    if (this.displayAsTag && !this.isCompoundFieldMember) {
      this.displayType = this.layoutDisplayType.TAG;

    } else if (this.displayAsText && !this.isCompoundFieldMember) {
      this.displayType = this.layoutDisplayType.TEXT;

    } else {
      this.subscribeToInlineError();
      this.displayType = this.layoutDisplayType.DATE;
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  public onNgModelChange(value) {
    this.modelField.toDataValue(value, this.transformOptions);
  }

  // private methods
  private setOptions() {
    let type = this.specField.type;
    this.pickerOptions.mode = 'standard';

    if (this.timeStampDirective === LMTimeStampDirective.DISPLAY_DATE) {
      type = AppConstants.YYYYMMDD;
    }

    // TODO: need to support TIMESTAMP for hijri and gregorian
    if (type === AppConstants.YYYYMMDD && this.timeStampDirective === undefined) {
      if (this.field.hasOwnProperty('displayHijriDate')) {
        // TODO: //maf I was thinking after all the preliminary work in ep, etc that we would not need to pass a locale
        // here, but passing one is the only way to get the correct umalqura calendar. There may be some work yet in ep.
        // When the locale is not passed, I get the a calendar, but it is not the same as when the locale is used,
        // meaning the calendar has en-US month names and en-US days, but the year is like 1441.
        this.pickerOptions.locale = 'ar-SA';
        this.pickerOptions.calendarName = 'islamic-umalqura';

        this.transformOptions.locale = 'ar-SA';
        this.transformOptions.calendarName = 'islamic-umalqura';

      } else if (this.field.hasOwnProperty('displayGregorianDate')) {
        this.pickerOptions.calendarName = 'gregorian';
        this.transformOptions.calendarName = 'gregorian';
      }
    }

    switch (type) {
      // anniversary
      case AppConstants.MMDD:
        this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.month;
        break;

      // date and time
      case AppConstants.TIMESTAMP:
        this.pickerOptions.showTime = true;
        this.pickerOptions.showMonthYearPicker = true;
        this.pickerOptions.useCurrentTime = true;

        if (this.lmFieldServiceAdapter.lmFieldLayoutService.hasLimitToMinuteIncrements(this.field, this.specField)
          || this.lmFieldServiceAdapter.lmFieldLayoutService.isDisplayDateAndHoursAndMinutes(this.field, this.specField)) {
          this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.datetime;
          this.pickerOptions.minuteInterval = this.layoutObject.limitToMinuteIncrements;
        } else {
          this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.short + ' '
            + Soho.Locale.calendar().dateFormat.timestamp;
          this.pickerOptions.secondInterval = 1;
          this.pickerOptions.minuteInterval = 1;
        }
        break;

      // year only
      case AppConstants.YYYY:
        this.pickerOptions.dateFormat =  'yyyy';
        this.pickerOptions.showMonthYearPicker = true;
        this.pickerOptions.hideDays = true;
        break;

      // period / birthday
      case AppConstants.YYYYMM:
        this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.year;
        this.pickerOptions.showMonthYearPicker = true;
        this.pickerOptions.hideDays = true;
        break;

      // year, month, day
      case AppConstants.YYYYMMDD:
        this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.short;
        this.pickerOptions.showMonthYearPicker = true;
        break;
    }
  }

  private subscribeToTimezoneField() {
    if (this.specField.type === AppConstants.TIMESTAMP && this.layoutObject.timeZoneIs) {
      this.transformOptions.timeZone = this.model.getField(this.layoutObject.timeZoneIs).value;
      this.subscriptionArray.push(this.model.timeZoneChangedEvent$
        .subscribe(({ bindId, timeZone, previousTimeZone }) => {
          if (bindId === this.layoutObject.timeZoneIs) {
            this.transformOptions.timeZone = previousTimeZone;
            const displayValue = this.lmFieldServiceAdapter.lmFormatUtilService.toDisplayValue(
              this.modelField.value, this.specField.type, this.transformOptions);

            this.transformOptions.timeZone = timeZone;
            this.modelField.value = this.lmFieldServiceAdapter.lmFormatUtilService.toDataValue(
              displayValue, this.specField.type, this.transformOptions);
          }
        }));
    }
  }

  onDateChange() {
    if (!this.hasFieldChanged) {
      // came back from the picker - user did not type in the input
      // lm-field-base has a change host listener that is
      // handling user input (typing in a value)
      this.onChange();
      this.handleOnFieldChanged();
    }
  }

  onFiltered(event: SohoFieldFilteredEvent) {
    if (event.filterOption.value === 'in-range') {
      this.mode = 'range';

      if (this.specField.type === AppConstants.TIMESTAMP) {
        this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.short + ' '
          + Soho.Locale.calendar().dateFormat.timestamp;
        this.pickerOptions.showTime = true;
      } else {
        this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.short;
        this.pickerOptions.hideDays = false;
        this.pickerOptions.showTime = false;
      }
    } else {
      this.mode = 'standard';

      switch (this.specField.type) {
        // date and time
        case AppConstants.TIMESTAMP:
          this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.short + ' '
            + Soho.Locale.calendar().dateFormat.timestamp;
          this.pickerOptions.showTime = true;
          break;

        // year only
        case AppConstants.YYYY:
          this.pickerOptions.dateFormat =  'yyyy';
          this.pickerOptions.showMonthYearPicker = true;
          this.pickerOptions.hideDays = true;
          break;

        // period / birthday
        case AppConstants.YYYYMM:
          this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.year;
          this.pickerOptions.showMonthYearPicker = true;
          this.pickerOptions.hideDays = true;
          break;

        // year, month, day
        case AppConstants.YYYYMMDD:
          this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.short;
          this.pickerOptions.showMonthYearPicker = true;
          break;

        // Anniversary
        case AppConstants.MMDD:
          this.pickerOptions.dateFormat = Soho.Locale.calendar().dateFormat.month;
          break;
      }
    }
    super.onFiltered(event);
  }
}
